import {
  useState,
  createContext,
  useContext,
  useCallback,
  useEffect,
} from "react";

import { AxiosError } from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import FullScreenInit from "components/FullScreenInit";

import EmailVerification from "containers/Onboarding/components/EmailVerification";

import { useGetWorkspacesQuery } from "api/workspace/queries/useGetWorkspacesQuery";
import { useGetWorkspaceQuery } from "api/workspace/queries/useGetWorkspaceQuery";
import { checkAccess } from "utils/userAccess";

interface WorkspacesContextStore {
  workspaces: Workspace[];
  isWorkspacesLoading: boolean | undefined;
  activeWorkspace: Workspace;
  setActiveWorkspace: (w: Workspace) => void;
  changeWorkspace: (s: string) => void;
  recentWorkspaces: RecentWorkspace[];
  setRecentWorkspaces: (recentWorkspace: RecentWorkspace[]) => void;
  addRecentWorkspace: (name: string, slug: string) => void;
}

const initialStore = {
  workspaces: [],
  isWorkspacesLoading: false,
  activeWorkspace: {
    id: "",
    name: "",
    slug: "",
    description: "",
    availableCredits: 0,
    featureFlags: [],
    createdBy: "",
    createdAt: "",
    updatedBy: "",
    updatedAt: "",
    role: "READER" as WorkspaceRole,
  },
  setActiveWorkspace: () => { },
  changeWorkspace: () => { },
  recentWorkspaces: [],
  setRecentWorkspaces: () => { },
  addRecentWorkspace: () => { },
};

const WorkspacesContext = createContext<WorkspacesContextStore>(initialStore);

interface WorkspacesProviderProps {
  children: React.ReactNode;
  allowPublic?: boolean;
}

const WorkspacesProvider = ({
  children,
  allowPublic = false,
}: WorkspacesProviderProps) => {
  const { workspaceSlug: workspaceSlugFromParams } = useParams();
  const [searchParams] = useSearchParams();
  const workspaceSlugFromSearchParams = searchParams.get("workspaceSlug");

  const workspaceSlugFromURL =
    workspaceSlugFromParams || workspaceSlugFromSearchParams || "";
  /**
   * If allowPublic is true, we will get the workspace from the workspaceSlugFromURL
   * Otherwise, we will use the workspaces owned by the user
   */
  const {
    data: dataWithStatus,
    error: err,
    isLoading,
  } = allowPublic ? {} : useGetWorkspacesQuery();
  const {
    data: publicWorkspace,
    error: publicErr,
    isLoading: isLoadingPublic,
  } = allowPublic ? useGetWorkspaceQuery(workspaceSlugFromURL) : {};
  const error = err || publicErr as AxiosError;
  const { data: workspaces } = dataWithStatus || publicWorkspace || {};
  const navigate = useNavigate();
  const [activeWorkspace, setActiveWorkspace] = useState<Workspace>(
    initialStore.activeWorkspace
  );
  const [recentWorkspaces, setRecentWorkspaces] = useState<RecentWorkspace[]>(
    []
  );

  const setAccessibleRecentWorkspaces = useCallback(async () => {
    try {
      const recentWs = localStorage.getItem("recentPublicWorkspaces") || "";

      const recentWsArr = JSON.parse(recentWs);

      if (recentWsArr && Array.isArray(recentWsArr)) {
        setRecentWorkspaces(
          recentWsArr.filter((ws) =>
            workspaces?.find((w) => w.slug === ws.slug)
          )
        );
      }
    } catch {
      setRecentWorkspaces([]);
    }
  }, [workspaces]);

  useEffect(() => {
    setAccessibleRecentWorkspaces();
  }, [setAccessibleRecentWorkspaces]);

  useEffect(() => {
    localStorage.setItem(
      "recentPublicWorkspaces",
      JSON.stringify(recentWorkspaces)
    );
  }, [activeWorkspace.slug, recentWorkspaces]);

  // useEffect(() => {
  //   console.debug("errior", error?.cause);
  // }, [error]);

  const addRecentWorkspace = useCallback((name: string, slug: string) => {
    setRecentWorkspaces((r) => {
      const workspaceIndex = r.findIndex((item) => item.slug === slug);

      if (workspaceIndex >= 0) {
        r[workspaceIndex] = { name, slug };
        return [...r];
      } else {
        return [...r, { name, slug }];
      }
    });
  }, []);

  const changeWorkspace = useCallback(
    (workspaceSlug) => {
      const workspace = workspaces?.find((item) => item.slug === workspaceSlug);

      if (workspace) {
        navigate(`/${workspaceSlug}`);
      }
    },
    [navigate, workspaces]
  );

  useEffect(() => {
    const workspace = workspaces?.find(
      (item) => item.slug === workspaceSlugFromURL
    );

    if (workspace) {
      setActiveWorkspace(workspace);
    }
  }, [workspaces, workspaceSlugFromURL]);

  useEffect(() => {
    if (isLoading || allowPublic) return;
    const workspacesAccessibleToUser = workspaces?.filter(
      (workspace) => checkAccess("READER", workspace.role)
    );
    if (!workspacesAccessibleToUser || (workspacesAccessibleToUser.length === 0)) {
      navigate("/onboarding", {
        relative: "route",
      });
    }
  }, [isLoading, navigate, workspaces]);

  // catch email unverified (469 error - custom error code for email unverified)
  if (error && error?.response?.status === 469) {
    return <EmailVerification />;
  }

  if (isLoading || isLoadingPublic || (error && error?.response?.status !== 469 && !allowPublic)) {
    return (
      <FullScreenInit
        hideBackButton
        loading={isLoading || isLoadingPublic}
        animation="error"
        displayText={error?.message}
      />
    );
  }

  return (
    <WorkspacesContext.Provider
      value={{
        workspaces: workspaces || [],
        isWorkspacesLoading: isLoading || isLoadingPublic,
        activeWorkspace,
        setActiveWorkspace,
        changeWorkspace,
        recentWorkspaces,
        setRecentWorkspaces,
        addRecentWorkspace,
      }}
    >
      {children}
    </WorkspacesContext.Provider>
  );
};

const useWorkspaces = () => useContext(WorkspacesContext);

export { WorkspacesProvider, useWorkspaces };
